//-- Copyright (c) 2021 TMT Insights Group, LLC
import { parseDate } from '@/_utils/date';
import { convertStylesToStyled } from '@/_utils/utils';
import { ConfigContext } from '@/contextAPI/configContext';
import styled from '@emotion/styled';
import { Button, Menu, MenuItem } from '@mui/material';
import { addDays, format } from 'date-fns';
import { MouseEvent, useContext, useState } from 'react';
import { selectCurrentCycle, selectCycleDates } from '../../redux/selectors/dashboardSelectors';
import { setCurrentCycle } from '../../redux/slices/dashboardSlice';
import PolarisIcon from '../_common/PolarisIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { CustomCycleDate } from '@/__configs/config_types';

const DashboardDateDropdown = () => {
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const currentCycle = useAppSelector(selectCurrentCycle);
  const cycleDates = useAppSelector(selectCycleDates);
  const customCycleDates = (useContext(ConfigContext)?.dashboardConfig?.customCycleDates || []) as CustomCycleDate[];

  let selection;
  if (currentCycle === 'All') {
    selection =  'All Months';
  } else if (typeof currentCycle === 'object') {
    selection = currentCycle.label;
  } else {
    // we add 1 day to avoid TZ issues.
    selection = format(addDays(Date.parse(currentCycle), 1), 'MMMM yyyy');
  }

  const handleChange = (e: MouseEvent<HTMLLIElement, globalThis.MouseEvent> & { target: { id: string } }) => {
    dispatch(setCurrentCycle(e.target.id));
    handleClose();
  };

  const handleCustomCycledateClicked = (customCycleDate: CustomCycleDate) => {
    dispatch(setCurrentCycle(customCycleDate));
    handleClose();
  }

  const handleClose = () => setAnchorEl(null);
  const handleOpen = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent> & { currentTarget: any }) => setAnchorEl(e.currentTarget);

  return (
    <div>
      <SelectionButton aria-controls="cycle-date-menu" aria-haspopup="true" onClick={handleOpen}>
        {selection}
        <PolarisIcon icon="down" height={16} />
      </SelectionButton>
      <StyledMenu
        id="cycle-date-menu"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem id="All" onClick={handleChange}>
          All Months
        </MenuItem>
        {customCycleDates.map((cycleDate) => (
          <MenuItem key={cycleDate.label} onClick={() => handleCustomCycledateClicked(cycleDate)} id={cycleDate.id}>
            {cycleDate.label}
          </MenuItem>
        ))}
        {cycleDates.map((cycleDate) => (
          <MenuItem key={cycleDate} value={cycleDate} onClick={handleChange} id={cycleDate}>
            {format(parseDate(cycleDate), 'MMMM yyyy')}
          </MenuItem>
        ))}
      </StyledMenu>
    </div>
  );
};

export default DashboardDateDropdown;

const SelectionButton = styled(Button)`
  ${({ theme }) => convertStylesToStyled(theme.typography.h6)}

  text-transform: none;
  letter-spacing: 0;

  & .MuiButton-label {
    line-height: 1;
  }

  & svg {
    font-size: 1.7rem;
    margin-left: ${({ theme }) => theme.spacing(1)};
  }
`;

const StyledMenu = styled(Menu)`
  max-height: 400px;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;
